import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { BsSearch, BsX, BsGrid3X3Gap, BsBriefcase, BsPerson, BsBuilding, BsCalendarEvent } from 'react-icons/bs';

import { crmSearch } from '#crmFeature/api/crmApi';
import IconButton from '#globalComponents/IconButton/IconButton';
import Panel from '#globalComponents/Overlay/Panel';
import { LAYER } from '#lib/constants/layers';
import { STALE_TIMES } from '#lib/queryConstants';

// Constants

const CATEGORIES = [
	{ key: null, label: 'Alle Kategorien', icon: BsGrid3X3Gap },
	{ key: 'deals', label: 'Deals', icon: BsBriefcase },
	{ key: 'companies', label: 'Unternehmen', icon: BsBuilding },
	{ key: 'activities', label: 'Aktivitäten', icon: BsCalendarEvent },
];

const TYPE_ICONS = {
	deal: BsBriefcase,
	contact: BsPerson,
	company: BsBuilding,
	activity: BsCalendarEvent,
};

const PER_PAGE = 20;
const DEBOUNCE_MS = 300;

// Helpers

import { formatEuro } from '../../../utils/formatters';

/**
 * Render HTML snippet with <mark> tags as React elements.
 * Typesense returns highlights wrapped in <mark>…</mark>.
 */
function renderHighlight(html) {
	if (!html) return null;
	const parts = html.split(/(<mark>.*?<\/mark>)/g);
	return parts.map((part, i) => {
		const match = part.match(/^<mark>(.*)<\/mark>$/);
		if (match) {
			return (
				<mark key={i} className="bg-caution-200 text-inherit rounded-sm">
					{match[1]}
				</mark>
			);
		}
		return part;
	});
}

/** Get the subtitle line for a result based on its type. */
function getSubtitle(type, data) {
	switch (type) {
		case 'deal': {
			const parts = [data.companyNameSnapshot, data.status].filter(Boolean);
			return parts.join(' · ');
		}
		case 'contact': {
			const parts = [data.companyName, data.companyRole, data.emailAddress].filter(Boolean);
			return parts.join(' · ');
		}
		case 'company': {
			const parts = [data.addressCity, data.phoneNumber].filter(Boolean);
			return parts.join(' · ');
		}
		case 'activity': {
			const parts = [data.activityType, data.companyName, data.dealTitle].filter(Boolean);
			return parts.join(' · ');
		}
		default:
			return '';
	}
}

/** Get the display title for a result. */
function getTitle(type, data) {
	switch (type) {
		case 'deal':
			return data.title;
		case 'contact':
			return data.fullName || data.emailAddress || '—';
		case 'company':
			return data.companyName;
		case 'activity':
			return data.title;
		default:
			return '';
	}
}

/** Get navigation target for a result. */
function getNavigationPath(type, data) {
	switch (type) {
		case 'deal':
			return data.crmCompanyId ? `/crm/companies/${data.crmCompanyId}` : null;
		case 'contact':
			return data.crmCompanyId ? `/crm/companies/${data.crmCompanyId}` : null;
		case 'company':
			return `/crm/companies/${data.id}`;
		case 'activity':
			return data.crmCompanyId ? `/crm/companies/${data.crmCompanyId}` : null;
		default:
			return null;
	}
}

// Debounce hook

function useDebouncedValue(value, delay) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debounced;
}

// Component

const CrmSearchBar = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [activeCategory, setActiveCategory] = useState(null);
	const [activeIdx, setActiveIdx] = useState(-1);

	const overlayInputRef = useRef(null);
	const sentinelRef = useRef(null);
	const resultsContainerRef = useRef(null);

	const debouncedQuery = useDebouncedValue(query.trim(), DEBOUNCE_MS);
	const isSearchable = debouncedQuery.length >= 2;

	// React Query infinite query
	const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading } = useInfiniteQuery({
		queryKey: ['crm-search', debouncedQuery, activeCategory],
		queryFn: async ({ pageParam = 1 }) => {
			return crmSearch({
				q: debouncedQuery,
				type: activeCategory,
				page: pageParam,
				perPage: PER_PAGE,
			});
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			return lastPage.hasMore ? lastPage.page + 1 : undefined;
		},
		enabled: isOpen && isSearchable,
		staleTime: STALE_TIMES.FREQUENT,
	});

	// Flatten all pages into a single results array
	const results = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flatMap((p) => p.results ?? []);
	}, [data]);

	const total = data?.pages?.[0]?.total ?? 0;
	const isFirstLoad = isLoading && isSearchable;

	// Overlay controls

	const openOverlay = useCallback(() => {
		setIsOpen(true);
		setActiveIdx(-1);
	}, []);

	const closeOverlay = useCallback(() => {
		setIsOpen(false);
		setQuery('');
		setActiveCategory(null);
		setActiveIdx(-1);
		// Clear search cache when overlay closes to save memory
		queryClient.removeQueries({ queryKey: ['crm-search'] });
	}, [queryClient]);

	// Focus overlay input when it opens
	useEffect(() => {
		if (isOpen) {
			setTimeout(() => overlayInputRef.current?.focus(), 50);
		}
	}, [isOpen]);

	// Escape key closes overlay
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e) => {
			if (e.key === 'Escape') closeOverlay();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [isOpen, closeOverlay]);

	// Reset active index when results change
	useEffect(() => {
		setActiveIdx(-1);
	}, [debouncedQuery, activeCategory]);

	// IntersectionObserver for infinity scroll

	useEffect(() => {
		if (!isOpen || !sentinelRef.current) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage();
				}
			},
			{ root: resultsContainerRef.current, rootMargin: '200px' },
		);
		observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, [isOpen, hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Handlers

	const handleSelect = (item) => {
		const path = getNavigationPath(item.type, item.data);
		if (path) navigate(path);
		closeOverlay();
	};

	const handleKeyDown = (e) => {
		if (results.length === 0) return;
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveIdx((i) => Math.min(i + 1, results.length - 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveIdx((i) => Math.max(i - 1, 0));
		} else if (e.key === 'Enter' && activeIdx >= 0) {
			e.preventDefault();
			handleSelect(results[activeIdx]);
		}
	};

	const handleCategoryChange = (key) => {
		setActiveCategory(key);
		setActiveIdx(-1);
		overlayInputRef.current?.focus();
	};

	// Render

	return (
		<>
			<div className="relative">
				<button
					type="button"
					onClick={openOverlay}
					className="flex items-center gap-2 w-full text-sm border border-bgBase-500 rounded-card pl-3 pr-9 py-2 text-bgBase-800 bg-white hover:border-bgBase-600 transition-colors cursor-text text-left"
				>
					Suchen …
				</button>
				<span className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 bg-bgBase-400 rounded-control pointer-events-none">
					<BsSearch className="w-3.5 h-3.5 text-bgBase-800" />
				</span>
			</div>

			{isOpen && (
				<div className={`fixed inset-0 ${LAYER.modal} flex`}>
					<div className="absolute inset-0 bg-text/40" onClick={closeOverlay} />

					<Panel className="relative mx-auto mt-16 mb-16 w-full max-w-3xl max-h-[calc(100vh-8rem)]">
						<div className="flex items-center gap-3 px-5 py-3.5 border-b border-bgBase-400">
							<BsSearch className="w-4 h-4 text-bgBase-800 flex-shrink-0" />
							<input
								ref={overlayInputRef}
								type="text"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Deals, Kontakte, Firmen, Aktivitäten suchen …"
								className="flex-1 border-0 bg-transparent p-0 rounded-none text-sm outline-none focus:ring-0 placeholder:text-bgBase-800"
								autoComplete="off"
							/>
							{(isFirstLoad || (isFetching && !isFetchingNextPage)) && (
								<div className="w-4 h-4 border-2 border-bgBase-500 border-t-primary-500 rounded-full animate-spin flex-shrink-0" />
							)}
							{query && !isFetching && (
								<IconButton
									icon={BsX}
									label="Suche leeren"
									onClick={() => {
										setQuery('');
										overlayInputRef.current?.focus();
									}}
									className="h-6 w-6"
								/>
							)}
							<button
								type="button"
								onClick={closeOverlay}
								className="ml-1 text-xs text-bgBase-800 bg-bgBase-400 rounded-control px-1.5 py-0.5 hover:bg-bgBase-500"
							>
								ESC
							</button>
						</div>

						<div className="flex flex-1 min-h-0">
							<div className="w-48 border-r border-bgBase-400 py-2 flex-shrink-0 overflow-y-auto">
								{CATEGORIES.map((cat) => {
									const Icon = cat.icon;
									const isActive = activeCategory === cat.key;
									return (
										<button
											key={cat.key ?? 'all'}
											type="button"
											onClick={() => handleCategoryChange(cat.key)}
											className={`w-full flex items-center gap-2.5 px-4 py-2 text-left text-sm transition-colors ${
												isActive
													? 'bg-primary-100 text-primary-700 font-medium'
													: 'text-text hover:bg-bgBase-300'
											}`}
										>
											<Icon className="w-4 h-4 flex-shrink-0" />
											{cat.label}
										</button>
									);
								})}
							</div>

							<div ref={resultsContainerRef} className="flex-1 overflow-y-auto">
								{!isSearchable && !isFetching && (
									<div className="px-6 py-12 text-center text-sm text-bgBase-800">
										Mindestens 2 Zeichen eingeben …
									</div>
								)}

								{isFirstLoad && (
									<div className="flex justify-center py-12">
										<div className="w-6 h-6 border-2 border-bgBase-500 border-t-primary-500 rounded-full animate-spin" />
									</div>
								)}

								{isSearchable && !isFetching && results.length === 0 && (
									<div className="px-6 py-12 text-center text-sm text-bgBase-800">
										Keine Ergebnisse für &bdquo;{debouncedQuery}&ldquo;
									</div>
								)}

								{results.length > 0 && (
									<div>
										<div className="px-4 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-bgBase-800">
											{total} Ergebnis{total !== 1 ? 'se' : ''}
										</div>

										{results.map((item, idx) => {
											const Icon = TYPE_ICONS[item.type] || BsSearch;
											const title = getTitle(item.type, item.data);
											const subtitle = getSubtitle(item.type, item.data);
											return (
												<button
													key={`${item.type}-${item.id}-${idx}`}
													type="button"
													onClick={() => handleSelect(item)}
													className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${
														activeIdx === idx ? 'bg-primary-100' : 'hover:bg-bgBase-300'
													}`}
												>
													<Icon className="w-4 h-4 text-bgBase-800 flex-shrink-0 mt-0.5" />
													<div className="flex-1 min-w-0">
														<p className="text-sm font-medium text-text truncate">
															{title}
														</p>
														{subtitle && (
															<p className="text-[11px] text-bgBase-800 truncate mt-0.5">
																{subtitle}
															</p>
														)}
														{item.highlight && (
															<p className="text-[11px] text-bgBase-800 truncate mt-0.5">
																{renderHighlight(item.highlight)}
															</p>
														)}
													</div>
													{item.type === 'deal' && item.data.dealValue > 0 && (
														<span className="text-xs text-bgBase-800 flex-shrink-0 mt-0.5">
															{formatEuro(item.data.dealValue)}
														</span>
													)}
												</button>
											);
										})}

										<div ref={sentinelRef} className="h-1" />

										{isFetchingNextPage && (
											<div className="flex justify-center py-3">
												<div className="w-5 h-5 border-2 border-bgBase-500 border-t-primary-500 rounded-full animate-spin" />
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</Panel>
				</div>
			)}
		</>
	);
};

export default CrmSearchBar;
